// ONE widget, TWO modes, THREE hosts.
//
// A rough drawing of the Figma frame Loom read, built from bytes Loom already
// stores — no Figma call is spent to show it, and re-opening it costs nothing.
// The difference between the two modes is ONE prop:
//
//   `askId` PRESENT  — a flow step. The user is asked "Is this the screen you
//                      meant?" and answers yes or no. That is the whole gate:
//                      it confirms IDENTITY and nothing else.
//   `askId` ABSENT   — chat. Nothing to answer, no gate, no verdict controls;
//                      the same drawing, on request, under the chip.
//
// The hosts are the Focus refine step (`RefineFlow`), a chat chip
// (`FocusChat` → `FigmaLinkPanel`), and the issue hub (`IssuePage`), which is a
// second flow surface and takes question mode exactly as the step does. The hub
// is the reason the boundary tokens ride the WIDGET and not `.loom-focus`: it is
// `.loom-dashboard`, which defines no `--ff-figma-draw`, and a letterform
// boundary that holds on two surfaces out of three is not a boundary. Before it
// had a renderer the ask fell through to the hub's free-text pin, where the
// user's approval SENTENCE went onto the bus as a `user_message`.
//
// Everything between those two lines is shared, which is what "modular" has to
// mean here: the risk was never two renderers, it was two PAYLOAD paths, so
// every host gets the scene from the same GET through the same hook.
//
// WHAT APPROVING IT MEANS, and the boundary that keeps it honest. "Loom read
// the screen I meant" — nothing more. It is not evidence for a role, an
// acceptance criterion, a definition of done or a point estimate; no field
// greens, no slot changes, nothing enters the readiness card. The verdict frame
// deliberately carries NO text field: an approval sentence would be echoed onto
// the bus as a `user_message` and become matchable by the gate's substring
// quote scan, which has no word boundaries.
//
// CHROME IMITATION. A design can legitimately contain the sentence "Connect
// your GitLab account again", so escaping is beside the point; five structural
// defences are what actually hold, and each is testable:
//   (a) Loom owns the headline — a `focus.*` catalog key, never a payload
//       string and never `ask.question`;
//   (b) nothing in the drawing is focusable (`tabIndex={-1}` container, zero
//       interactive descendants);
//   (c) the drawing does not take pointer events, so a click inside it does
//       nothing at all;
//   (d) every control is a SIBLING outside the border, labelled from the
//       catalog;
//   (e) a visible, labelled boundary the design cannot reach — a bordered box,
//       a persistent "Aus Figma" caption chip ON that border, and drawn text in
//       `--ff-figma-draw`, a family deliberately neither `--ff-sans` nor
//       `--ff-mono` so "this is a picture of someone else's screen" is
//       pre-attentive.
// The payload tightening does most of the work: layer names never reach this
// process, so the imitation surface is only text the design actually displays —
// exactly what a screenshot would show.

import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { useT, usePlural, type MessageKey } from "../../i18n/index.js";
import { useFormatNumber, useRelativeTime } from "../../i18n/format.js";

import { drawScene } from "./sceneRender";
import {
  PREVIEW_FLOW_MAX_H,
  useDesignScene,
  type DesignScene,
  type PreviewStatus,
} from "./useDesignScene";

export type PreviewVerdict = "right" | "wrong";

export interface FigmaPreviewProps {
  /** The socket's session. `null` renders the no-drawing state, never a throw. */
  readonly sessionId: string | null;
  /**
   * The opaque handle this host was HANDED — never one it built. In a flow step
   * that is the ask's own `subject.previewId`; under a chat chip it is the
   * chip's `linkId`. The client cannot turn either into a file key, which is
   * what keeps this from being a door to drawing an arbitrary design.
   */
  readonly previewId: string | null;
  /**
   * THE PICTURE THIS CHAT HAD, when the chat has one: `ws.figmaSceneFor(handle)`,
   * folded from the replayed `figma_scene` event. Non-null and the widget never
   * fetches — the drawing is the bytes this conversation actually read, not the
   * newest copy of the frame.
   *
   * `null`/absent keeps the route, which is still right for a chat from before
   * the event existed and for a scene the event log's cap evicted. Nothing else
   * in this file branches on it: the caption reads `scene.readAtIso`, which
   * rides inside the draw list on both lanes, so the widget cannot phrase the
   * two differently.
   */
  readonly scene?: DesignScene | null;
  /** PRESENT ⇒ question mode. Absent ⇒ chat mode. The only difference. */
  readonly askId?: string;
  /** Question mode only. */
  readonly onVerdict?: (verdict: PreviewVerdict) => void;
  /** Question mode only — the optional skip, and the single control offered
   *  when the drawing cannot be built. */
  readonly onSkip?: () => void;
  /**
   * The frame's name, for the chat subtitle. It comes from the CHIP's own React
   * state (`link.frameName`), never from the scene: that is what lets the
   * payload stay name-free while the caption still says which screen this is.
   */
  readonly frameLabel?: string;
  /** Height budget, CSS px. Flow steps get less than chat — a question is on
   *  the screen with them. */
  readonly maxH?: number;
}

/**
 * The host's measured content width.
 *
 * Measured rather than computed. The design's arithmetic for a 400px viewport
 * was wrong by 12px (the flow column's side padding comes off the VIEWPORT, on
 * `.focus-shell`, not off `.focus-col`), and any such number goes stale the
 * moment a wrapper's padding changes. `ResizeObserver` is absent in jsdom and
 * in older WebKit, so the fallback chain is: observer → mount measurement +
 * window resize → a column-width constant.
 */
const FALLBACK_AVAIL_W = 608;

const useContentWidth = (
  ref: React.RefObject<HTMLElement>,
): number => {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const measure = (): void => {
      const w = el.clientWidth;
      setWidth((prev) => (Math.abs(prev - w) < 1 ? prev : w));
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ref]);
  return width > 0 ? width : FALLBACK_AVAIL_W;
};

/**
 * The bordered box's own horizontal chrome — padding plus border, both sides.
 *
 * `.fig-prev-frame` declares `padding: 14px` and a 1px border, so the drawing
 * has 30px less room than the box it sits in. That number was a hard-coded 24
 * and drew 6px wider than its container, which the frame's `overflow: hidden`
 * then silently cut off — invisible to a test that asserts against the `availW`
 * it handed in rather than against a real content box. So it is READ off the
 * element, in the same `useLayoutEffect` pass that measures the width: a
 * padding change in the stylesheet cannot desynchronise it, and nothing here
 * has to restate a value CSS already owns.
 *
 * Not circular: padding and border do not depend on the content, so measuring
 * them cannot feed back into the size of the thing measured. The fallback is
 * only ever used for the render before the frame exists.
 */
const FRAME_CHROME_FALLBACK = 30;

const useFrameChrome = (
  ref: React.RefObject<HTMLElement>,
  /** The frame only exists once there is something to draw, so the effect has
   *  to re-run when it appears — `ref.current` alone never re-triggers one. */
  mounted: boolean,
): number => {
  const [chrome, setChrome] = useState(FRAME_CHROME_FALLBACK);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!mounted || el === null || typeof getComputedStyle !== "function") {
      return undefined;
    }
    const measure = (): void => {
      const cs = getComputedStyle(el);
      const n = (v: string): number => {
        const parsed = Number.parseFloat(v);
        return Number.isFinite(parsed) ? parsed : 0;
      };
      const total =
        n(cs.paddingLeft) +
        n(cs.paddingRight) +
        n(cs.borderLeftWidth) +
        n(cs.borderRightWidth);
      // A measured ZERO is not a measurement. jsdom applies no stylesheet and
      // returns "" for every unset longhand, and a real browser can be asked
      // before layout — in both, the honest reading of 0 on a box the stylesheet
      // gives 14px of padding and a 1px border is "not measured yet", so the
      // fallback stands. The cost if a future design really does set both to 0
      // is a drawing 30px narrower than it could be; the cost of trusting it is
      // a drawing 30px wider than its container, silently clipped.
      if (total <= 0) return;
      setChrome((prev) => (Math.abs(prev - total) < 0.5 ? prev : total));
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    return undefined;
  }, [ref, mounted]);
  return chrome;
};

/**
 * Below this host width the drawing and its controls stack; at or above it they
 * sit SIDE BY SIDE.
 *
 * Measured on the HOST, never on the viewport, because the two hosts are not
 * the same width at the same viewport: the flow column is 640px and a chat chip
 * sits in whatever the message row leaves. A media query would get one of them
 * wrong.
 *
 * The side-by-side is what makes "one question per screen" survive a portrait
 * frame. A 375x812 screen fitted to a 400px height budget is 185px wide, so in
 * one column it spends 400px of vertical room and leaves 455px of the 640px
 * column empty — and every control lands under it. Measured in headless Chrome
 * against the real `focus.css` at 1280x800 (a 582px stage): stacked, the step
 * is 751px tall and Bigger / Yes / No / Carry on all sit below the fold. Beside
 * it, the same drawing at the same size leaves the answer on screen.
 */
const SIDE_BY_SIDE_MIN_W = 560;

/** The share of the host the DRAWING may take when the two sit side by side.
 *  The rest is the aside, which has to hold two buttons on one row. */
const SHOT_SHARE = 0.5;

/**
 * The height budget when the two STACK AND THERE IS AN ANSWER UNDER THE
 * PICTURE — a phone-width flow step, or the hub's question column.
 *
 * Stacked, the drawing is directly above the answer instead of beside it, so
 * every pixel of picture is a pixel the answer moves down. Measured at 430x900
 * (an 813px viewport) with the flow's own chrome and composer: at the full
 * 400px budget the step is 796px tall and "Carry on" lands below the fold; at
 * this budget it does not. A cap, never a floor — a host that already asked for
 * less keeps its own number.
 *
 * WHICH IS WHY IT IS CONDITIONED ON THE QUESTION, NOT ON THE LAYOUT. In chat
 * there is nothing under the picture to push down: no verdict buttons, no skip,
 * nothing to answer. Applied there too, it capped a 375x812 screen at 139x300
 * inside a 412px chip — a drawing whose text is unreadable at any viewport
 * width, and the chat budget (`PREVIEW_CHAT_MAX_H`, 520) was dead code that
 * nothing could ever reach. Below the side-by-side width a chat sketch takes
 * the whole budget it was handed: ~240x520, the same picture, legible.
 */
const STACKED_MAX_H = 300;

/**
 * The text the screen displays — ONE ROW PER TEXT LAYER, in draw order.
 *
 * Strictly more useful to a screen-reader user than the picture is, and the
 * reason the `<svg>` can be `aria-hidden` without hiding anything.
 *
 * The rows are layers, not lines, because every other number on this widget
 * counts layers: the chip says "Text layers: 7", the `aria-label` says "7
 * pieces of text", and both are the digest's own count. `node.lines` is the
 * server's WRAP of one layer into the box Figma reported for it, so listing
 * lines turned one confirmation paragraph into five rows and a reader who
 * counted the list got 11 against the 7 Loom had just told them. A widget
 * whose whole claim is "every number on it is backed by the payload" cannot
 * publish two counts of the same thing.
 *
 * Re-joined with a single space, which is exactly what `wrapGreedy`
 * (`@loom/shared/figma/project.ts`) split on. The one lossy case is a word
 * longer than the box, which that fitter hard-breaks by code point — so a
 * 30-character word in a narrow layer reads with one space in it. A space
 * inside a rare long word is a smaller lie than a row count that contradicts
 * the label above it.
 */
const textLayersOf = (scene: DesignScene): readonly string[] => {
  const out: string[] = [];
  for (const node of scene.nodes) {
    if (node.kind !== "text") continue;
    const layer = (node.lines ?? [])
      .map((line) => line.trim())
      .filter((line) => line !== "")
      .join(" ");
    if (layer !== "") out.push(layer);
  }
  return out;
};

/**
 * Where the enlarged drawing is MOUNTED, which is not where it is declared.
 *
 * `.fig-prev-modal-scrim` is `position: fixed; inset: 0`, and that resolves
 * against the viewport only while NO ancestor has a transform, filter or
 * containment — any one of those makes that ancestor the containing block for
 * fixed descendants instead. The flow column is exactly such an ancestor:
 * `.focus-col.focus-anim` ran its entrance animation with `both`, so the final
 * keyframe's `transform: translateY(0)` stayed on it forever. Measured on a
 * 950px viewport, the scrim came out at top -512 / height 1300 in chat and top
 * 142 / height 592 in a flow step, with the top 272px (resp. 130px) of the
 * drawing above the viewport and nothing to scroll — the enlarged view showed
 * LESS of the screen than the inline one, which inverts the control's whole
 * purpose. The stylesheet no longer leaves that transform behind; this is the
 * other half, and it is the half that does not depend on every future ancestor
 * staying transform-free.
 *
 * The host is the SCOPE ROOT, never `document.body`: `--surface`, `--ink-*`,
 * `--shadow-lg` and the rest are declared on `.loom-focus` / `.loom-dashboard`,
 * so a modal parented to `<body>` would lose the whole palette. Both roots are
 * untransformed (`.loom-focus` is itself `position: fixed; inset: 0`), so the
 * scrim resolves against the viewport from either. `document.body` is the
 * fallback for a host that is neither — a bare test render — where there are no
 * tokens to lose.
 */
const OVERLAY_HOST_SELECTOR = ".loom-focus, .loom-dashboard";

/**
 * One sentence per way of having no drawing — and `lost` is a THIRD one.
 *
 * `empty` says "only the words were kept for this design, not the layout",
 * which is about the BYTES. That sentence was being published over a `lost`
 * handle too, where it is simply false: the bytes are usually still on the row,
 * and the sibling frame chip draws a picture off exactly that row. What is gone
 * is the in-process map from this chip to the read it was about. The route
 * already answers the two with different `reason` tokens (`no_raw` /
 * `no_drawing` against `unknown_link` / `unknown_session`); `useDesignScene`
 * keeps them apart, and this is the half that says so.
 *
 * `loading` never reaches here — the branch above owns it — but it is listed so
 * the map is total over `PreviewStatus` and a new state cannot be added without
 * a sentence for it.
 */
const NO_SKETCH_COPY: Readonly<Record<PreviewStatus, MessageKey>> = {
  loading: "focus.figma.preview.loading",
  ready: "focus.figma.preview.noDrawing",
  empty: "focus.figma.preview.noDrawing",
  lost: "focus.figma.preview.lostHandle",
  failed: "focus.figma.preview.failed",
};

const overlayHostFor = (el: HTMLElement | null): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  return el?.closest<HTMLElement>(OVERLAY_HOST_SELECTOR) ?? document.body;
};

/**
 * The claim lines under a drawing, in ONE order for both surfaces.
 *
 * `sketchNote` first — it is the calibration everything else is read against —
 * then THIS rendering's crop line, then one line per compromise above zero.
 * The crop belongs to the fit it was measured on and to no other: the modal
 * fits to the viewport and the panel to its host, so a drawing cropped in one
 * is often whole in the other. That is why `cropped` is a parameter and not a
 * member of `claims`.
 *
 * One renderer, so the enlarged view cannot come to claim something the inline
 * one does not, or to order the same lines differently.
 */
const ClaimLines = ({
  claims,
  cropped,
}: {
  readonly claims: readonly string[];
  readonly cropped: boolean;
}) => {
  const t = useT();
  return (
    <>
      {claims.map((line, i) => (
        <Fragment key={line}>
          <p className="fig-prev-claim">{line}</p>
          {i === 0 && cropped && (
            <p className="fig-prev-claim">{t("focus.figma.preview.topOnly")}</p>
          )}
        </Fragment>
      ))}
    </>
  );
};

interface BiggerProps {
  readonly scene: DesignScene;
  readonly title: string;
  /**
   * The counted claim the drawing makes, in the panel's own order —
   * `sketchNote` first, then one line per compromise above zero.
   *
   * Passed in rather than recomputed: this is the surface where the drawing is
   * largest and most convincing and the "Aus Figma" badge most authoritative,
   * which makes it the surface that most needs the counts. Built once by the
   * widget, so the enlarged view cannot come to claim something the inline one
   * does not.
   */
  readonly claims: readonly string[];
  /**
   * WHEN the bytes under this picture were read, already worded.
   *
   * Same argument the claims arrived on, and it applies at least as strongly:
   * the enlarged view is the most convincing rendering of someone else's
   * screen Loom ever puts up, and "which bytes am I looking at" is the one
   * question a convincing picture makes more urgent, not less. Worded by the
   * widget so the two surfaces cannot phrase the same timestamp two ways.
   */
  readonly readAtLine?: string;
  readonly onClose: () => void;
  readonly children?: ReactNode;
}

/**
 * The drawing at 1:1 (or fit-to-viewport, whichever is smaller).
 *
 * It REPLACES the screen rather than competing with it, which is how
 * one-question-per-screen survives an expand: in question mode the two verdict
 * buttons repeat inside, so someone who had to look closer can answer from
 * where they are looking instead of dismissing the modal first.
 */
const BiggerModal = ({
  scene,
  title,
  claims,
  readAtLine,
  onClose,
  children,
}: BiggerProps) => {
  const t = useT();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnTo = useRef<Element | null>(null);
  // Its OWN gradient ids: the panel's drawing is still in the document under
  // the scrim, and two `<linearGradient id>`s with one id paint both shapes
  // with whichever comes first.
  const drawId = useId();
  useEffect(() => {
    returnTo.current = document.activeElement;
    // `preventScroll`, because focusing an element inside a scrollable box
    // SCROLLS IT INTO VIEW. In question mode on a phone the panel is taller
    // than the viewport (the two verdict buttons stack full-width below the
    // picture), so the open focus scrolled the panel down and put the "From
    // Figma" badge — the one always-visible marker that this is someone else's
    // screen — off the top edge, at the exact moment the design is shown
    // largest. Measured at 400x800 against the real stylesheet: badge top 0
    // with the plain focus, 22 with this one, and the drawing starting at 25
    // against 47. The picture is what the reader opened this for.
    //
    // It is belt AND braces now rather than the only thing holding: the row
    // Close sits in is the FIRST thing under the picture (see the body below),
    // so at 400x800 it measures 656..756 with every claim line rendered and
    // needs no scrolling into view at all. The guard stays because the panel
    // still overflows — a future line above the row must not be able to open
    // the dialog scrolled past the badge.
    closeRef.current?.focus({ preventScroll: true });
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") {
        // `stopImmediatePropagation`, not `stopPropagation`: both listeners
        // here are on `window`, and `stopPropagation` does not stop the OTHER
        // handlers on the same target. Harmless today (the only other Focus
        // window-Escape handler is gated on `railOpen`), wrong the moment a
        // second one is not.
        ev.stopImmediatePropagation();
        onClose();
        return;
      }
      if (ev.key !== "Tab") return;
      // The dialog claims `aria-modal`, the scrim stops the mouse — this stops
      // the KEYBOARD. Without it Tab walks out of the modal and onto the step
      // behind a 45%-opacity scrim, where a focus ring lands on a control the
      // user cannot see and Enter fires it for real. Same trap, same reason, as
      // the rail drawer's (`focus/ui.tsx`); re-read per keystroke because the
      // verdict buttons inside are conditional on the mode.
      const panel = panelRef.current;
      if (panel === null) return;
      const stops = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      const first = stops[0] ?? panel;
      const last = stops[stops.length - 1] ?? panel;
      const active = document.activeElement;
      if (active === null || !panel.contains(active)) {
        ev.preventDefault();
        first.focus();
      } else if (ev.shiftKey && active === first) {
        ev.preventDefault();
        last.focus();
      } else if (!ev.shiftKey && active === last) {
        ev.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const back = returnTo.current;
      if (back instanceof HTMLElement) back.focus();
    };
  }, [onClose]);
  // Viewport-bounded, never scene-bounded: a 375x2400 page at 1:1 would be
  // taller than any screen, so the same crop rule applies here — bigger, not
  // unbounded.
  const vw = typeof window === "undefined" ? 960 : window.innerWidth;
  const vh = typeof window === "undefined" ? 720 : window.innerHeight;
  const drawn = drawScene(scene, {
    availW: Math.max(160, vw - 96),
    maxH: Math.max(200, vh - 220),
    idPrefix: drawId,
  });
  return (
    <div className="fig-prev-modal-scrim" onClick={onClose}>
      <div
        className="fig-prev-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={panelRef}
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* The badge is a sibling of the clipped box, not a child of it — see
            `.fig-prev-boundary`. Inside, the frame's own `overflow: hidden`
            cut 9px off the one marker that says "someone else's screen". */}
        <div className="fig-prev-boundary is-big">
          <span className="fig-prev-badge">{t("focus.figma.preview.fromFigma")}</span>
          <div className="fig-prev-frame">
            <div className="fig-prev-draw" tabIndex={-1}>
              {drawn.svg}
            </div>
          </div>
        </div>
        {/*
         * THE ANSWER FIRST, THE READING AFTER IT — and only in this modal.
         *
         * In the panel the claims sit ABOVE the verdict, because that is where
         * the calibration is first read and it must be read before the user
         * commits. In here it has already been read: this dialog is only ever
         * opened FROM that panel. What the modal owes instead is the thing it
         * exists for — "someone who had to look closer can answer from where
         * they are looking" — and claim lines above the row take that away.
         *
         * Measured in Chromium at 400x800 against the real `focus.css`, the
         * picture at its own 240x580 fit, with the claims above the row:
         * `sketchNote` alone put `.fig-prev-acts` at 708..808 and Close 8px
         * below the fold; all four lines put it at 823..923, and the crop line
         * on top of those at 855..955 — 155px below a fold the dialog does not
         * scroll on its own. With the row here it measures 656..756 in every
         * one of those combinations, the read line and the crop line included.
         *
         * `.fig-prev-claim` is a global rule, not an aside-scoped one, so the
         * same lines wear the same type here with no stylesheet of their own.
         */}
        <div className="fig-prev-acts">
          {children}
          <button type="button" className="focus-btn is-ghost" onClick={onClose} ref={closeRef}>
            {t("focus.figma.preview.closeBigger")}
          </button>
        </div>
        {readAtLine !== undefined && (
          <span className="fig-prev-read">{readAtLine}</span>
        )}
        <ClaimLines claims={claims} cropped={drawn.fit.cropped} />
      </div>
    </div>
  );
};

export const FigmaPreview = ({
  sessionId,
  previewId,
  scene: persistedScene = null,
  askId,
  onVerdict,
  onSkip,
  frameLabel,
  maxH = PREVIEW_FLOW_MAX_H,
}: FigmaPreviewProps) => {
  const t = useT();
  const tn = usePlural();
  const fmt = useFormatNumber();
  // The SAME formatter the chip's own meta line uses for "read 2 days ago", so
  // the two timestamps a reader sees within 40px of each other cannot be
  // phrased two different ways.
  const rel = useRelativeTime();
  const hostRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  // Gradient ids are document-wide; see `DrawOptions.idPrefix`.
  const drawId = useId();
  const hostW = useContentWidth(hostRef);
  // The overlay's MOUNT POINT doubles as the open flag: non-null is open. One
  // piece of state, and the host is resolved at the moment of opening, when the
  // widget is certainly in the document — `closest` on a detached node is null.
  const [overlayHost, setOverlayHost] = useState<HTMLElement | null>(null);
  const { status, scene } = useDesignScene(
    sessionId,
    previewId,
    persistedScene,
  );
  const asking = askId !== undefined;
  const frameChrome = useFrameChrome(frameRef, scene !== null);
  // Beside the controls, or above them. Decided on the HOST's measured width so
  // the same widget lands right in a 640px flow column and under a chat chip.
  const side = hostW >= SIDE_BY_SIDE_MIN_W;

  const openBigger = useCallback(() => {
    setOverlayHost(overlayHostFor(hostRef.current));
  }, []);
  // Stable, because `BiggerModal`'s keyboard effect lists `onClose` in its deps:
  // a fresh arrow every render would tear down and re-arm the focus trap on each
  // parent render, stealing focus back to Close every time.
  const closeBigger = useCallback(() => setOverlayHost(null), []);

  // A drawing that is gone takes its own modal with it — otherwise a re-fetch
  // that 404s would leave an empty dialog over the screen.
  useEffect(() => {
    if (scene === null) setOverlayHost(null);
  }, [scene]);

  // The drawing's own box, in CSS px. Side by side the picture takes a SHARE of
  // the host and the aside takes the rest; stacked it takes the whole width.
  // `frameChrome` is the bordered box's real padding + border, read off the
  // element — the constant it replaced was 24 against a 30px box, so a
  // width-bound design drew 6px wider than its container and the frame's
  // `overflow: hidden` quietly cut the difference off.
  const availW = Math.max(
    120,
    Math.round(side ? hostW * SHOT_SHARE : hostW) - frameChrome,
  );
  // The stacked cap is about the ANSWER the picture would push off screen, so
  // it applies only where there is one. In chat (`asking` false) nothing sits
  // under the drawing, and the host's own budget stands.
  const drawH = side || !asking ? maxH : Math.min(maxH, STACKED_MAX_H);
  const drawn = useMemo(
    () =>
      scene === null
        ? null
        : drawScene(scene, { maxH: drawH, availW, idPrefix: drawId }),
    [scene, drawH, availW, drawId],
  );
  const textRows = useMemo(
    () => (scene === null ? [] : textLayersOf(scene)),
    [scene],
  );

  const verdict = useCallback(
    (v: PreviewVerdict) => {
      setOverlayHost(null);
      onVerdict?.(v);
    },
    [onVerdict],
  );

  // The two verdict controls, rendered in the step AND repeated inside the
  // modal. One definition, so they cannot drift apart.
  const verdictActs = asking ? (
    <>
      <button type="button" className="focus-btn is-ok" onClick={() => verdict("right")}>
        {t("focus.figma.preview.yes")}
      </button>
      <button type="button" className="focus-btn is-ghost" onClick={() => verdict("wrong")}>
        {t("focus.figma.preview.no")}
      </button>
    </>
  ) : null;

  /**
   * What this sketch is called, on the chat subtitle and as the enlarged
   * dialog's accessible name.
   *
   * THE FALLBACK IS THE POINT, and it must never be "fixed" by putting the name
   * back on the wire. `frameLabel` comes from the chip's live React state, and
   * a design's file / folder / frame names are LIVE-ONLY:
   * `redactEventForPersistence` strips `FIGMA_LINK_NAME_FIELDS` on the way into
   * both durable logs, so a chip replayed after an instance recycle has no
   * `frameName` at all. Rendered only when a name exists, the subtitle then
   * vanished and a restored panel read "WHAT LOOM READ" and then nothing —
   * the widget stopped saying what it was in exactly the situation where the
   * reader is least sure. A name-free sentence says it without one.
   *
   * TWO chips arrive nameless and the widget cannot tell them apart, which is
   * why `chatSubtitleUnnamed` says "a screen" and not "the screen". One is the
   * replayed frame chip above. The other is a WHOLE-FILE chip: it addresses the
   * latest frame read inside the file, and `read-state.ts`'s rule 2 withholds a
   * frame name from it on purpose — the chip is about the FILE, and a sketch
   * labelled from one frame would name a screen that chip never claims. It may
   * have three frame reads behind it, so a definite article there asserts a
   * singularity it does not have.
   */
  const named =
    frameLabel !== undefined && frameLabel.trim() !== "" ? frameLabel : null;
  const sketchName =
    named !== null
      ? t("focus.figma.preview.chatSubtitle", { frame: named })
      : t("focus.figma.preview.chatSubtitleUnnamed");

  const header = asking ? (
    <>
      <span className="fig-prev-kicker">{t("focus.figma.preview.kicker")}</span>
      {/* Loom's OWN sentence, from the catalog — never `ask.question`, never a
          payload string. Defence (a): the question the user is answering can
          never be authored by the design. */}
      <h2 className="fig-prev-question">{t("focus.figma.preview.question")}</h2>
    </>
  ) : (
    <>
      <span className="fig-prev-title">{t("focus.figma.preview.chatTitle")}</span>
      {/* ALWAYS rendered — named when the chip still knows the name, name-free
          when the replay took it. See `sketchName`. */}
      <span className="fig-prev-sub">{sketchName}</span>
    </>
  );

  // No drawing, and no dead end. In question mode the ONE control is the
  // optional skip, relabelled: a sketch that cannot render must never be a step
  // the user has no way past.
  if (status !== "ready" || scene === null || drawn === null) {
    return (
      <section className="fig-prev" ref={hostRef} aria-busy={status === "loading"}>
        {header}
        {status === "loading" ? (
          <>
            <div
              className="fig-prev-skeleton"
              style={{ height: `${Math.round(maxH * 0.6)}px` }}
              aria-hidden="true"
            />
            <span className="fig-prev-note" role="status">
              {t("focus.figma.preview.loading")}
            </span>
          </>
        ) : (
          <span className="fig-prev-note">{t(NO_SKETCH_COPY[status])}</span>
        )}
        {asking && status !== "loading" && onSkip !== undefined && (
          <div className="fig-prev-acts">
            <button type="button" className="focus-btn is-ghost" onClick={onSkip}>
              {t("focus.figma.preview.continue")}
            </button>
          </div>
        )}
      </section>
    );
  }

  const shapes = Math.max(0, scene.counts.drawn - scene.counts.texts);
  /**
   * The read timestamp, only if it is one.
   *
   * The mirror schema is permissive by design (`z.string()`), so `readAtIso`
   * can arrive as any string at all — and `Intl.RelativeTimeFormat.format(NaN)`
   * THROWS a RangeError, which in a render is the whole widget replaced by a
   * blank. A line that cannot be trusted is not drawn; the drawing is.
   */
  const readAt =
    scene.readAtIso !== undefined && Number.isFinite(Date.parse(scene.readAtIso))
      ? scene.readAtIso
      : null;
  // Worded ONCE. The caption row and the enlarged view are two renderings of
  // the same fact about the same bytes, and a second `t(...)` call site is how
  // they come to word it two ways.
  const readAtLine =
    readAt === null
      ? undefined
      : t("focus.figma.preview.readAt", { when: rel(readAt) });
  // The CLAIM this drawing makes is counted, not promised. On a well-made
  // design the first line reads "6 shapes"; on a file of `Rectangle 47` it
  // reads "42", and the user can calibrate their own trust instead of being
  // told a fixed level of quality that is a lie on one of those two files.
  // `sketchNote` FIRST — it is the calibration everything else is read
  // against — then one line per compromise above zero. ONE array, rendered in
  // the panel and repeated inside Bigger, so the enlarged view cannot come to
  // claim something the inline one does not.
  const claims: string[] = [t("focus.figma.preview.sketchNote")];
  if (scene.counts.plainBoxes > 0) {
    claims.push(tn("plural.figmaPlainBoxes", scene.counts.plainBoxes));
  }
  if (scene.counts.images > 0) {
    claims.push(tn("plural.figmaImagesOmitted", scene.counts.images));
  }
  if (scene.counts.hiddenOmitted > 0) {
    claims.push(tn("plural.figmaHiddenOmitted", scene.counts.hiddenOmitted));
  }

  return (
    <section className="fig-prev" ref={hostRef}>
      {header}
      {/*
       * THE DRAWING AND ITS ANSWER, BESIDE EACH OTHER.
       *
       * A phone frame is portrait: 375x812 fitted to a 400px height budget is
       * 185px wide, so stacked it spends the whole fold on a picture and leaves
       * 455px of a 640px column empty — with every control under it. Measured
       * in headless Chrome against the real `focus.css` at 1280x800 (a 582px
       * stage): 751px of step, and Bigger / Yes / No / Carry on all below the
       * fold. That is "one question per screen" broken in its sharpest form —
       * the question is answerable only after a scroll.
       *
       * Beside it, the same drawing at the same size keeps the answer on
       * screen. Under `SIDE_BY_SIDE_MIN_W` the two stack again, which is the
       * right shape for a phone and is where a scroll is expected anyway.
       */}
      <div className={"fig-prev-body" + (side ? " is-side" : "")}>
        <div className="fig-prev-shot">
          {/* The badge is a SIBLING of the clipped box, never a child of it.
              `.fig-prev-frame` keeps `overflow: hidden` + `contain` (a node with
              a corrupt width must not paint over the column), and inside that
              the chip — which sits 0.6rem ABOVE the border on purpose — was cut
              in half: 9px of the one always-visible marker that says "this is
              someone else's screen". */}
          <div className="fig-prev-boundary">
            {/* Persistent, on the border. Not a hover affordance and not a
                tooltip: the boundary has to be readable at the same instant the
                drawing is. */}
            <span className="fig-prev-badge">{t("focus.figma.preview.fromFigma")}</span>
            <div className="fig-prev-frame" ref={frameRef}>
              {/*
               * `role="img"` + a Loom-authored `aria-label`. An `aria-label`
               * built from design strings would be chrome imitation delivered
               * straight into a screen reader with NO boundary at all — border,
               * caption and letterform all vanish in the audio channel. The
               * words themselves are below, in the `<details>`, where they are
               * announced as content.
               */}
              <div
                className="fig-prev-draw"
                role="img"
                tabIndex={-1}
                aria-label={t("focus.figma.preview.aria", {
                  w: fmt(Math.round(scene.w)),
                  h: fmt(Math.round(scene.h)),
                  texts: fmt(scene.counts.texts),
                  shapes: fmt(shapes),
                })}
              >
                {drawn.svg}
              </div>
            </div>
          </div>
          {/* The picture's own caption row: how big the screen is, and the one
              control that is about the PICTURE rather than about the question.
              Keeping Bigger here leaves the aside's row to the answer alone —
              two buttons, one line — instead of a four-button row in which a
              view control sits next to "Yes, that's it". */}
          <div className="fig-prev-cap">
            <span className="fig-prev-size">
              {t("focus.figma.preview.size", {
                w: fmt(Math.round(scene.w)),
                h: fmt(Math.round(scene.h)),
              })}
            </span>
            {/* WHEN the bytes under this picture were read. The drawing is
                always the NEWEST stored bytes for the frame, so a chat from
                last week can show today's design under a chip that still says
                "read 2 days ago" — and nothing on screen said which. Absent
                (an older server) renders nothing at all, never an empty line. */}
            {readAtLine !== undefined && (
              <span className="fig-prev-read">{readAtLine}</span>
            )}
            <button
              type="button"
              className="focus-btn is-ghost fig-prev-zoom"
              onClick={openBigger}
            >
              {t("focus.figma.preview.bigger")}
            </button>
          </div>
        </div>
        <div className="fig-prev-aside">
          {/* What the picture IS, before the answer rather than after it: it is
              the calibration the question depends on ("a sketch, not a
              screenshot", `claims[0]`), so it must be read before the user
              commits. The crop line belongs to THIS fit, not to the modal's,
              which is why `cropped` is passed rather than folded into the
              array both surfaces share. */}
          <ClaimLines claims={claims} cropped={drawn.fit.cropped} />
          {asking && <div className="fig-prev-acts">{verdictActs}</div>}
          {asking && onSkip !== undefined && (
            <div className="focus-skip-optional">
              <button type="button" className="focus-btn is-ghost" onClick={onSkip}>
                {t("focus.figma.preview.continue")}
              </button>
            </div>
          )}
          {textRows.length > 0 && (
            // Closed, and UNDER the answer: a drawing can say where a thing
            // sits but never what it is, so this is what a screen-reader user
            // actually needs — reference, not the question.
            <details className="fig-prev-words">
              <summary>{t("focus.figma.preview.textList")}</summary>
              <ul>
                {textRows.map((row, i) => (
                  <li key={`${i}-${row}`}>{row}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
      {overlayHost !== null &&
        createPortal(
          <BiggerModal
            scene={scene}
            // A dialog that fills the screen names what it shows, and names it
            // the same way the panel's own subtitle does — including when the
            // replay took the frame's name away.
            title={sketchName}
            claims={claims}
            {...(readAtLine !== undefined ? { readAtLine } : {})}
            onClose={closeBigger}
          >
            {verdictActs}
          </BiggerModal>,
          overlayHost,
        )}
    </section>
  );
};
